import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ProductType } from '@econtractor/types';
import * as apiProduct from 'api/apiProducts';
import * as apiWarehouse from 'api/apiWarehouses';
import { useToast } from 'context/ToastContext';
import type { ProductSelectOption } from '../types';

export interface ProductsSelectProps {
    title?: string;
    onChange?: (option: ProductSelectOption | null) => void;
    autoChangeId?: number | string | null;
    value?: number | string | null;
    onlyWarehouseId?: number | null;
    disabled?: boolean;
    setIsLoading?: (loading: boolean) => void;
    required?: boolean;
    /** Лише монтування (без UI) — прогрів модуля / i18n */
    preload?: boolean;
}

const toOption = (product: ProductType): ProductSelectOption => ({
    value: product.ID,
    label: product.prodName,
    vendor: product.vendorName,
    article: product.prodArticle,
    defPrice: product.prodDefPrice,
    defPriceSale: product.prodDefPriceSale,
});

const ProductNameItem = ({ product }: { product: ProductSelectOption }) => {
    const titleMeta = [product.article, product.vendor].filter(Boolean).join(' · ');

    return (
        <div className="min-w-0 overflow-hidden flex-grow-1">
            <h6 className="mb-1 text-truncate" title={product.label}>
                {product.label || '—'}
            </h6>
            {titleMeta ? (
                <p className="f-12 mb-0 text-truncate" title={titleMeta}>
                    {product.article ? <span className="text-muted">{product.article}</span> : null}
                    {product.article && product.vendor ? <span className="text-muted"> · </span> : null}
                    {product.vendor ? <span className="text-primary">{product.vendor}</span> : null}
                </p>
            ) : null}
        </div>
    );
};

const ProductsSelect = ({
    title,
    onChange,
    autoChangeId,
    value,
    onlyWarehouseId = null,
    disabled = false,
    setIsLoading,
    required = true,
    preload = false,
}: ProductsSelectProps) => {
    const { t } = useTranslation(['Products', 'common']);
    const { showToast } = useToast();
    const [search, setSearch] = useState('');
    const [options, setOptions] = useState<ProductSelectOption[]>([]);
    const [selected, setSelected] = useState<ProductSelectOption | null>(null);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const warehouseIdsRef = useRef<number[] | null>(null);
    const selectedIdRef = useRef<number | string | null>(null);
    const autoAppliedRef = useRef<string | number | null>(null);
    const loadGenRef = useRef(0);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    selectedIdRef.current = selected?.value ?? null;

    const isFixed = Boolean(selected);
    const showResults = loading || searched;

    const setLoadingBoth = (next: boolean) => {
        setLoading(next);
        if (setIsLoading) setIsLoading(next);
    };

    const beginLoad = () => {
        loadGenRef.current += 1;
        const gen = loadGenRef.current;
        setLoadingBoth(true);
        return gen;
    };

    const endLoad = (gen: number) => {
        if (gen !== loadGenRef.current) return;
        setLoadingBoth(false);
    };

    const getWarehouseIds = async (): Promise<number[] | null> => {
        if (onlyWarehouseId === null || onlyWarehouseId === undefined) return null;
        if (warehouseIdsRef.current) return warehouseIdsRef.current;
        const ids = await apiWarehouse.getProductsInWarehouse(onlyWarehouseId);
        warehouseIdsRef.current = Array.isArray(ids) ? ids.map(Number).filter((id) => id > 0) : [];
        return warehouseIdsRef.current;
    };

    const resolveProductOption = async (productId: number | string): Promise<ProductSelectOption | null> => {
        const normalizedId = Number(productId);
        if (!Number.isFinite(normalizedId) || normalizedId <= 0) return null;
        const warehouseIds = await getWarehouseIds();
        if (warehouseIds !== null && !warehouseIds.includes(normalizedId)) {
            return null;
        }
        const product = await apiProduct.getProduct(normalizedId);
        return product ? toOption(product) : null;
    };

    const fetchProducts = async () => {
        if (disabled || loading || isFixed) return;

        const gen = beginLoad();

        const warehouseIds = await getWarehouseIds();
        if (warehouseIds !== null && warehouseIds.length === 0) {
            setOptions([]);
            setSearched(true);
            endLoad(gen);
            return;
        }

        const res = await apiProduct.getList({
            search: (search || '').trim(),
            page: 1,
            onPage: 50,
            status: [1],
            order: 'order by prod_name',
            ...(warehouseIds !== null ? { Ids: warehouseIds } : {}),
        });

        if (gen !== loadGenRef.current) return;
        setSearched(true);
        endLoad(gen);

        if (!res) {
            setOptions([]);
            showToast(t('common:messError'), 'danger');
            return;
        }

        setOptions((res.products || []).map(toOption));
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            fetchProducts();
        }
    };

    const handleSelect = (option: ProductSelectOption) => {
        if (disabled) return;
        setSelected(option);
        onChange?.(option);
        setSearch('');
        setOptions([]);
        setSearched(false);
    };

    const handleClear = () => {
        if (disabled) return;
        setSelected(null);
        onChange?.(null);
        setSearch('');
        setOptions([]);
        setSearched(false);
    };

    useEffect(() => {
        warehouseIdsRef.current = null;
    }, [onlyWarehouseId]);

    // Синхронізація відображення з value (ID)
    useEffect(() => {
        if (preload) return;

        const syncFromValue = async () => {
            if (value == null || value === '') {
                setSelected(null);
                return;
            }
            if (Number(selectedIdRef.current) === Number(value)) return;

            const gen = beginLoad();
            const option = await resolveProductOption(value);
            if (gen !== loadGenRef.current) return;
            setSelected(option);
            endLoad(gen);
        };

        void syncFromValue();
    }, [value, onlyWarehouseId, preload]);

    // Автовибір за autoChangeId (як у попередньому Select)
    useEffect(() => {
        if (preload) return;

        const normalizedAutoId = Number(autoChangeId);
        if (!Number.isFinite(normalizedAutoId) || normalizedAutoId <= 0) {
            autoAppliedRef.current = null;
            return;
        }
        if (Number(autoAppliedRef.current) === normalizedAutoId) return;

        autoAppliedRef.current = normalizedAutoId;

        const applyAuto = async () => {
            const gen = beginLoad();
            const option = await resolveProductOption(normalizedAutoId);
            if (gen !== loadGenRef.current) return;
            setSelected(option);
            onChangeRef.current?.(option);
            endLoad(gen);
            if (!option) {
                showToast(t('selectNoResults'), 'warning');
            }
        };

        void applyAuto();
    }, [autoChangeId, onlyWarehouseId, preload]);

    if (preload) return null;

    const labelText = title || t('selectTitle');

    return (
        <div className="user-selected mb-3">
            {labelText && (
                <div className="d-flex justify-content-between align-items-center">
                    <label className="form-label text-muted mb-1">{labelText}</label>
                </div>
            )}

            {isFixed ? (
                <div className="user-selected-fixed">
                    <div className="user-selected-fixed-user d-flex align-items-center gap-2 min-w-0">
                        <ProductNameItem product={selected!} />
                    </div>
                    <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="user-selected-clear"
                        disabled={disabled}
                        title={t('common:buttons.clear')}
                        onClick={handleClear}
                    >
                        <i className="bi bi-x-lg" />
                    </Button>
                </div>
            ) : (
                <div className={`user-selected-panel${showResults ? ' is-open' : ''}`}>
                    <InputGroup className="user-selected-search">
                        <Form.Control
                            type="text"
                            value={search}
                            disabled={disabled || loading}
                            required={required}
                            placeholder={t('selectSearchPlaceholder')}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        <Button
                            type="button"
                            variant="link"
                            className="user-selected-search-btn"
                            disabled={disabled || loading}
                            title={t('selectSearchBtn')}
                            onClick={fetchProducts}
                        >
                            {loading ? (
                                <Spinner animation="border" size="sm" variant="primary" />
                            ) : (
                                <i className="bi bi-search" />
                            )}
                        </Button>
                    </InputGroup>

                    {showResults && (
                        <div className="user-selected-results position-relative" style={{ maxHeight: 220 }}>
                            {loading && (
                                <div className="user-selected-empty">
                                    <Spinner animation="border" size="sm" variant="primary" className="opacity-75" />
                                </div>
                            )}

                            {!loading && options.length === 0 && (
                                <div className="user-selected-empty">
                                    <i className="bi bi-box-seam fs-5 d-block mb-1 opacity-50" />
                                    {t('selectNoResults')}
                                </div>
                            )}

                            {!loading && options.length > 0 && (
                                <ul className="user-selected-list">
                                    {options.map((option) => (
                                        <li key={option.value}>
                                            <button
                                                type="button"
                                                className="user-selected-item"
                                                disabled={disabled}
                                                onClick={() => handleSelect(option)}
                                            >
                                                <ProductNameItem product={option} />
                                                <i className="bi bi-chevron-right user-selected-item-chevron" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ProductsSelect;
